import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getScoreNum } from './shared';

const GRADIENT = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';

// ── Tier Color System (matches ScoreOverview) ─────────────────────────────────
const getRingColor = (n) => {
  if (n >= 85) return '#10F090';
  if (n >= 70) return '#22D3EE';
  if (n >= 50) return '#F59E0B';
  return '#B5325B';
};

const getBadgeColor = (n) => {
  if (n >= 85) return '#34D399';
  if (n >= 70) return '#67E8F9';
  if (n >= 50) return '#FBD05B';
  return '#FF7A5C';
};

const getScoreColorSolid = (n) => {
  if (n >= 85) return '#059669';
  if (n >= 70) return '#0891b2';
  if (n >= 50) return '#d97706';
  return '#e11d48';
};

const getScoreBgSolid = (n) => {
  if (n >= 85) return 'rgba(5,150,105,0.08)';
  if (n >= 70) return 'rgba(8,145,178,0.08)';
  if (n >= 50) return 'rgba(217,119,6,0.08)';
  return 'rgba(225,29,72,0.08)';
};

const getRingBg = (n) => {
  if (n >= 85) return 'rgba(16,240,144,0.10)';
  if (n >= 70) return 'rgba(34,211,238,0.10)';
  if (n >= 50) return 'rgba(245,158,11,0.10)';
  return 'rgba(255,87,51,0.10)';
};

const ResultsHeader = ({
  reportSelectorOpen, setReportSelectorOpen,
  startupData, currentReport, slidesData,
  iltcNum, auditReports, selectedReportId,
  handleReportChange, handleDownload,
  deckSlideCount,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth < 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsSmallMobile(window.innerWidth < 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const reportType = (currentReport?.report_type || '').toString().toUpperCase().trim();
  const isPaid     = reportType === 'PAID';
  const isFree     = reportType === 'FREE' || reportType === '';

  const analyzedCount = slidesData?.length || 0;
  const totalCount = isPaid
    ? analyzedCount
    : (deckSlideCount || Number(startupData?.slide_usage || startupData?.total_slides || startupData?.TOTAL_SLIDES || 0) || analyzedCount);

  const score     = Number(iltcNum) || 0;
  const ringColor = getRingColor(score);
  const badgeColor = getBadgeColor(score);
  const ringBg    = getRingBg(score);

  return (
    <div style={{
      background: '#ffffff', 
      borderRadius: 16, 
      padding: isMobile ? '10px 12px' : '0 20px',
      display: 'flex', 
      alignItems: isMobile ? 'flex-start' : 'center', 
      height: isMobile ? 'auto' : 54, 
      gap: 12,
      flexShrink: 0,
      boxShadow: '0 0.5px 0 rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.04)',
      flexDirection: isMobile ? 'column' : 'row',
      width: '100%',
      maxWidth: '100vw',
      boxSizing: 'border-box',
    }}>

      {/* ── Top Row on Mobile: Report Selector + Export Button ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 8 : 16,
        width: isMobile ? '100%' : 'auto',
        flexShrink: 0,
      }}>

      {/* ── Report Selector ── */}
      <div style={{ position: 'relative', flexShrink: 0, flex: isMobile ? 1 : 'none', minWidth: 0, maxWidth: 'fit-content' }}>
        <div
          onClick={() => setReportSelectorOpen(!reportSelectorOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10,
            padding: isMobile ? '6px 10px' : '6px 12px', 
            borderRadius: 10,
            border: `1px solid ${reportSelectorOpen ? '#004AAD' : 'rgba(0,0,0,0.06)'}`,
            background: '#ffffff', cursor: 'pointer', 
            minWidth: isSmallMobile ? 0 : 180,
            width: isMobile ? '100%' : 'auto',
            boxShadow: reportSelectorOpen ? '0 0 0 3px rgba(0,74,173,0.09)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          <div style={{
            width: isMobile ? 24 : 30, 
            height: isMobile ? 24 : 30, 
            borderRadius: 8,
            background: 'linear-gradient(135deg, rgba(0,74,173,0.10) 0%, rgba(0,130,251,0.10) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon name="FileText" size={isMobile ? 12 : 14} color="#004AAD" />
          </div>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            {/* Title — Sansation */}
            <div style={{ 
              fontFamily: "'Sansation', sans-serif", 
              fontSize: isMobile ? 11 : 12.5, 
              fontWeight: 700, 
              color: '#1d1d1f', 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              maxWidth: isSmallMobile ? 120 : 160,
            }}>
              {startupData?.deck_title || startupData?.startup_name || currentReport?.deck_name || 'Audit Report'}
            </div>
            {/* Subtitle — hidden on mobile */}
            {!isMobile && (
              <div style={{ fontFamily: 'inherit', fontSize: 10, color: '#aeaeb2' }}>
                v1.0 · {new Date(startupData?.report_date || currentReport?.published_at || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {analyzedCount}/{totalCount} slides
              </div>
            )}
          </div>
          <Icon name={reportSelectorOpen ? 'ChevronUp' : 'ChevronDown'} size={isMobile ? 14 : 16} color="#aeaeb2" />
        </div>

        {/* ── Report dropdown ── */}
        {reportSelectorOpen && auditReports.length > 1 && (
          <div style={{
            position: 'absolute', 
            top: 'calc(100% + 6px)', 
            left: 0,
            right: isMobile ? 0 : 'auto',
            width: isSmallMobile ? 'calc(100vw - 24px)' : (isMobile ? 280 : 340), 
            maxWidth: isSmallMobile ? 'calc(100vw - 24px)' : 340,
            background: '#ffffff',
            border: '1px solid rgba(0,0,0,0.06)', 
            borderRadius: 16,
            boxShadow: '0 12px 48px rgba(0,0,0,0.1)', 
            zIndex: 200, 
            overflow: 'hidden',
          }}>
            {/* Dropdown header — Sansation */}
            <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: "'Sansation', sans-serif", fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#aeaeb2' }}>Your Reports</span>
              <span style={{ fontFamily: 'inherit', fontSize: 10, color: '#aeaeb2', background: '#f8f8fa', padding: '2px 8px', borderRadius: 5 }}>{auditReports.length} reports</span>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {auditReports.map((report) => {
                const isSelected  = report.id === selectedReportId;
                const rScore      = report.total_score || 0;
                const scoreColor  = getScoreColorSolid(rScore);
                const scoreBg     = getScoreBgSolid(rScore);
                const rDate = new Date(report.published_at || report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const rName   = report.deck_name || report.company_name || `Audit ${rDate}`;
                const rType   = (report.report_type || '').toString().toUpperCase().trim();
                const rIsPaid = rType === 'PAID';
                return (
                  <div key={report.id} onClick={() => handleReportChange(report.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px', cursor: 'pointer',
                      borderBottom: '1px solid rgba(0,0,0,0.06)',
                      background: isSelected ? 'rgba(0,74,173,0.06)' : '#ffffff',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = '#ffffff'; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Report name — Sansation */}
                      <div style={{ 
                        fontFamily: "'Sansation', sans-serif", 
                        fontSize: 12, 
                        fontWeight: 700, 
                        color: '#1d1d1f', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        maxWidth: isSmallMobile ? 160 : 200,
                      }}>
                        {rName}
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 600, color: '#aeaeb2' }}>v1.0</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 600, color: '#aeaeb2' }}>{rDate}</span>
                        {rType && (
                          <span style={{
                            fontFamily: 'monospace', fontSize: 8, fontWeight: 700,
                            letterSpacing: '0.06em', textTransform: 'uppercase',
                            padding: '1px 6px', borderRadius: 4,
                            color:      rIsPaid ? '#004AAD' : '#1b8c2e',
                            background: rIsPaid ? 'rgba(0,74,173,0.09)' : 'rgba(27,140,46,0.08)',
                            border:     `0.5px solid ${rIsPaid ? 'rgba(0,74,173,0.18)' : 'rgba(27,140,46,0.20)'}`,
                          }}>
                            {rType}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{
                      fontFamily: "'Sansation', sans-serif", 
                      fontSize: 13, 
                      fontWeight: 800,
                      color: getScoreColorSolid(rScore), 
                      flexShrink: 0,
                    }}>
                      {rScore || '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Export Button (in same row on mobile) ── */}
      <div style={{ flexShrink: 0 }}>
        <button onClick={handleDownload}
          style={{ 
            fontFamily: "'Sansation', sans-serif", 
            fontSize: isMobile ? 10 : 11, 
            fontWeight: 700, 
            padding: isMobile ? '5px 10px' : '5px 12px', 
            borderRadius: 8, 
            border: 'none', 
            background: GRADIENT, 
            color: '#ffffff', 
            cursor: 'pointer', 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: isMobile ? 4 : 6,
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <Icon name="Download" size={isMobile ? 10 : 12} color="#ffffff" /> 
          Export
        </button>
      </div>

      </div>
      {/* End of top row wrapper */}

      {/* Divider - Desktop only */}
      {!isMobile && (
        <div style={{ width: 1, height: 28, background: 'rgba(0,0,0,0.06)', flexShrink: 0 }} />
      )}

      {/* ── Meta chips (below on mobile, inline on desktop) ── */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 8, 
        flex: 1, 
        minWidth: 0, 
        overflowX: 'auto',
        width: isMobile ? '100%' : 'auto',
        WebkitOverflowScrolling: 'touch',
      }}>

        {/* Date — inherit */}
        {(startupData?.report_date || currentReport?.published_at) && (
          <span style={{ 
            fontFamily: 'inherit', 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: 4, 
            fontSize: isMobile ? 10 : 11, 
            color: '#6e6e73', 
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            <Icon name="Calendar" size={isMobile ? 11 : 12} color="rgba(0,0,0,0.3)" />
            {new Date(startupData?.report_date || currentReport?.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}

        {/* Slides — label inherit, numbers Sansation */}
        {analyzedCount > 0 && (
          <span style={{ 
            fontFamily: 'inherit', 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: 4, 
            fontSize: isMobile ? 10 : 11, 
            color: '#6e6e73', 
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            <Icon name="Layers" size={isMobile ? 11 : 12} color="rgba(0,0,0,0.3)" />
            <span>
              <strong style={{ fontFamily: "'Sansation', sans-serif", fontWeight: 700, color: '#1d1d1f' }}>{analyzedCount}</strong>
              <span style={{ fontFamily: "'Sansation', sans-serif", fontWeight: 700, color: 'rgb(0,130,251)' }}>/{totalCount}</span>
              {!isMobile && (
                <span style={{ fontFamily: 'inherit', marginLeft: 3, fontWeight: 700, color: 'rgb(0,130,251)' }}>slides</span>
              )}
            </span>
          </span>
        )}

        {/* Industry — inherit - Hidden on mobile */}
        {startupData?.industry && !isMobile && (
          <span style={{ fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6e6e73', whiteSpace: 'nowrap' }}>
            <Icon name="Tag" size={12} color="rgba(0,0,0,0.3)" />
            {startupData.industry}
          </span>
        )}

        {/* Score — label inherit, number Sansation */}
        {score > 0 && (
          <span style={{ 
            fontFamily: 'inherit', 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: 4, 
            fontSize: isMobile ? 10 : 11, 
            color: '#6e6e73', 
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            <Icon name="Clock" size={isMobile ? 11 : 12} color="rgba(0,0,0,0.3)" />
            {!isMobile && 'Score: '}
            <strong style={{
              fontFamily: "'Sansation', sans-serif",
              fontWeight: 800,
              color: ringColor,
              textShadow: `0 0 8px ${ringColor}40`,
            }}>
              {score}
            </strong>
            <span style={{ fontFamily: "'Sansation', sans-serif", fontWeight: 700, color: 'rgb(0,130,251)' }}>/100</span>
          </span>
        )}

        {/* Deck tier badge — Sansation - Hidden on mobile */}
        {startupData?.deck_tier && !isMobile && (() => {
          const tier = startupData.deck_tier.toLowerCase();
          const tierScore = tier.includes('excellent') ? 90
            : tier.includes('good')     ? 75
            : tier.includes('critical') ? 30
            : 55;
          const tc = getBadgeColor(tierScore);
          return (
            <span style={{
              fontFamily: "'Sansation', sans-serif",
              display: 'inline-flex', padding: '2px 10px',
              borderRadius: 100, fontSize: 10, fontWeight: 700,
              color: getScoreColorSolid(tierScore),
              background: getScoreBgSolid(tierScore),
              border: `0.5px solid ${tc}40`,
            }}>
              {startupData.deck_tier}
            </span>
          );
        })()}

        {/* PAID / FREE badge - Hidden on mobile */}
        {(isPaid || isFree) && !isMobile && (
          <span style={{ fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
            <span style={{ fontFamily: 'inherit', fontSize: 11, color: '#6e6e73' }}>Audit Type:</span>
            {isPaid ? (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 100,
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                border: '1px solid rgba(250,204,21,0.30)',
                boxShadow: '0 0 8px rgba(250,204,21,0.15), inset 0 1px 0 rgba(255,255,255,0.07)',
                position: 'relative', overflow: 'hidden',
              }}>
                <span style={{
                  position: 'absolute', top: 0, left: '-100%', width: '60%', height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
                  animation: 'paid-shimmer 3s ease-in-out infinite', pointerEvents: 'none',
                }} />
                <style>{`@keyframes paid-shimmer { 0%,100% { left: -60% } 50% { left: 120% } }`}</style>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#facc15', boxShadow: '0 0 6px rgba(250,204,21,0.9)', flexShrink: 0 }} />
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M2 20h20M4 20L2 9l6 4 4-7 4 7 6-4-2 11H4z" stroke="#22c55e" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <circle cx="2" cy="9" r="1.5" fill="#22c55e"/>
                  <circle cx="12" cy="2" r="1.5" fill="#22c55e"/>
                  <circle cx="22" cy="9" r="1.5" fill="#22c55e"/>
                </svg>
                <span style={{ fontFamily: "'Sansation', sans-serif", fontSize: 10, fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#ffffff' }}>
                  PAID
                </span>
              </span>
            ) : (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 100,
                background: 'linear-gradient(135deg, #052e16 0%, #064e3b 50%, #065f46 100%)',
                border: '1px solid rgba(34,197,94,0.30)',
                boxShadow: '0 0 8px rgba(34,197,94,0.12), inset 0 1px 0 rgba(255,255,255,0.07)',
                position: 'relative', overflow: 'hidden',
              }}>
                <span style={{
                  position: 'absolute', top: 0, left: '-100%', width: '60%', height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
                  animation: 'paid-shimmer 3s ease-in-out infinite', pointerEvents: 'none',
                }} />
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 6px rgba(74,222,128,0.9)', flexShrink: 0 }} />
                <svg width={11} height={11} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="#4ade80" strokeWidth={2}/>
                  <path d="M7 11V7a5 5 0 0 1 9.9-1" stroke="#4ade80" strokeWidth={2} strokeLinecap="round"/>
                </svg>
                <span style={{ fontFamily: "'Sansation', sans-serif", fontSize: 10, fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#ffffff' }}>
                  FREE
                </span>
              </span>
            )}
          </span>
        )}

      </div>
    </div>
  );
};

export default ResultsHeader;